import { ReactNode } from 'react';
import styles from './Button.module.scss';
import { ButtonShared, ButtonSharedProps } from './ButtonShared';

interface Props {
  align?: 'start' | 'center' | 'end';
  icon?: ReactNode;
}

// Plain `Omit` doesn't distribute over `ButtonSharedProps`'s
// `AnchorProps | ButtonElementProps` union (`Omit`/`Pick` aren't
// conditional types), which would otherwise collapse the two branches'
// differing `href` types into one incompatible shape — so `className` is
// dropped from each branch separately instead.
type OmitDistributive<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

// `className` is intentionally not exposed: `Button` computes its own
// classes from `size`/`width`/`variant` and passes them to `ButtonShared`
// by name, and a consumer-supplied `className` spread after it would
// silently clobber that rather than merge with it.
type ButtonProps = Props & OmitDistributive<ButtonSharedProps, 'className'>;

export function Button({ ref, ...props }: ButtonProps) {
  const { size = 'medium', width = 'auto' } = props;

  const className = [
    styles[`button-size-${size}`],
    styles[`button-width-${width}`],
    props.variant === 'text' && styles['button-text'],
  ]
    .filter(Boolean)
    .join(' ');

  const iconClassName = [styles[`button-icon`]].filter(Boolean).join(' ');

  return (
    <ButtonShared ref={ref} className={className} {...props}>
      <span className={iconClassName}>{props.icon}</span>
      <span>{props.children}</span>
    </ButtonShared>
  );
}
